import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardAppShell from 'components/ui/DashboardAppShell';
import DashboardLayoutContent from 'components/ui/DashboardLayoutContent';
import PanelHeader from 'components/ui/PanelHeader';
import CrmBreadcrumb from 'components/ui/CrmBreadcrumb';
import Icon from 'components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { getCrmQuotes, updateCrmQuote, duplicateCrmQuote, formatQuoteNumber, formatInvoiceNumber, getQuoteDocLabel } from '../../services/crmService';
import { formatMoney } from '../../utils/formatMoney';

const STATUS_STYLES = {
  borrador: 'bg-gray-100 text-gray-600',
  enviado:  'bg-blue-100 text-blue-700',
  aceptado: 'bg-green-100 text-green-700',
  rechazado:'bg-red-100 text-red-700',
};
const STATUS_LABELS = { borrador: 'Borrador', enviado: 'Enviado', aceptado: 'Aceptado', rechazado: 'Rechazado' };
const EDITABLE_QUOTE_STATUSES = new Set(['borrador', 'enviado', 'pendiente']);

export default function CrmQuotes() {
  const navigate = useNavigate();
  const { business } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [search, setSearch] = useState('');

  const load = async () => {
    if (!business?.id) return;
    setLoading(true);
    const { data } = await getCrmQuotes(business.id);
    setQuotes(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [business?.id]);

  const fmt = (n) => formatMoney(n, business?.currency);

  // BUG-FIX: antes esta función ignoraba el error de updateCrmQuote y
  // siempre llamaba a load() -- si el UPDATE fallaba (RLS, red, etc.) la
  // UI recargaba la MISMA fila sin cambios y no pasaba nada visible. Ahora
  // se muestra el error (mecanismo ya usado en este mismo archivo antes
  // de QUOTE-TO-SALE-1, ver handleConvert histórico, y en CrmBarcodes.jsx)
  // y solo se refresca la lista si la fila devuelta confirma el nuevo
  // status.
  const handleStatus = async (id, status) => {
    setBusy(id + status);
    const { data, error } = await updateCrmQuote(id, { status });
    setBusy('');
    if (error) {
      alert('No se pudo actualizar el estado: ' + (error.message || 'error desconocido'));
      return;
    }
    if (data?.status !== status) {
      alert('No se pudo confirmar la actualización del estado. Intenta nuevamente.');
      return;
    }
    await load();
  };

  const handleDuplicate = async (id) => {
    setBusy(id + 'dup');
    const { data } = await duplicateCrmQuote(id);
    setBusy('');
    if (data?.id) navigate(`/crm/presupuestos/${data.id}`);
    else load();
  };

  // QUOTE-TO-SALE-1: "Crear nota de venta" NUNCA crea nada acá -- solo
  // navega al editor de una NV nueva, pasando el presupuesto de origen
  // como query param. El editor la precarga (cliente/ítems/condiciones)
  // pero la fila en crm_invoices recién se crea si el usuario pulsa
  // Guardar ahí. Aceptar un presupuesto jamás crea una nota de venta.
  const handleCreateInvoice = (q) => {
    navigate(`/crm/facturas/nueva?quote=${q.id}`);
  };

  const docLabel = getQuoteDocLabel(business?.documentTitleType);

  const filtered = search.trim()
    ? quotes.filter(q => {
        const query = search.trim().toLowerCase();
        return (
          formatQuoteNumber(q.quote_number, business?.documentTitleType).toLowerCase().includes(query) ||
          (q.wa_customers?.name || '').toLowerCase().includes(query) ||
          (q.created_at || '').slice(0, 10).includes(query)
        );
      })
    : quotes;

  return (
    <DashboardAppShell>
      <PanelHeader
        title={<><CrmBreadcrumb section={`${docLabel.title}s`} /><h1 className="text-base font-bold" style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-foreground)', letterSpacing: '-0.02em' }}>{docLabel.title}s</h1></>}
        subtitle={<p className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>{loading ? 'Cargando…' : `${quotes.length} ${quotes.length !== 1 ? docLabel.plural : docLabel.singular}`}</p>}
        mobileActions={
          <button
            onClick={() => navigate('/crm/presupuestos/nuevo')}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <Icon name="FilePlus" size={16} />
            {docLabel.nuevo}
          </button>
        }
      >
        <button
          onClick={() => navigate('/crm/presupuestos/nuevo')}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          <Icon name="FilePlus" size={16} />
          {docLabel.nuevo}
        </button>
      </PanelHeader>

      <DashboardLayoutContent>
        {!loading && quotes.length > 0 && (
          <div className="mb-4">
            <div className="relative">
              <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={`Buscar por número, cliente o fecha…`}
                className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}
        {loading ? (
          <div className="flex justify-center py-20">
            <Icon name="Loader2" size={32} className="animate-spin text-blue-500" />
          </div>
        ) : quotes.length === 0 ? (
          <div className="text-center py-20">
            <Icon name="FileText" size={48} className="mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500 font-medium">Todavía no hay {docLabel.plural}.</p>
            <button
              onClick={() => navigate('/crm/presupuestos/nuevo')}
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <Icon name="FilePlus" size={16} />Crear primer {docLabel.singular}
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="SearchX" size={36} className="mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500 text-sm">Sin resultados para <strong>"{search}"</strong></p>
            <button onClick={() => setSearch('')} className="mt-2 text-blue-600 text-sm hover:underline">Limpiar búsqueda</button>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(q => {
              const canEdit = EDITABLE_QUOTE_STATUSES.has(q.status) && !q.converted_to_invoice_id;
              return (
              <div key={q.id} className="bg-white border border-gray-200 rounded-xl px-4 py-3 hover:shadow-sm transition-shadow">
                <div className="flex items-start justify-between gap-3 min-w-0">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="font-bold text-gray-900 text-sm whitespace-nowrap">{formatQuoteNumber(q.quote_number, business?.documentTitleType)}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[q.status] || 'bg-gray-100 text-gray-600'}`}>
                        {STATUS_LABELS[q.status] || q.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 truncate">{q.wa_customers?.name || <em className="text-gray-400">Sin cliente</em>}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-bold text-gray-900 text-sm">{fmt(q.total)}</p>
                    <p className="text-xs text-gray-400">{new Date(q.created_at).toLocaleDateString('es-CL')}</p>
                  </div>
                </div>

                {/* Acciones compactas: misma jerarquía visual que Notas de venta. */}
                <div className="flex flex-wrap items-center gap-2 mt-2.5">
                  {canEdit && (
                    <button
                      onClick={() => navigate(`/crm/presupuestos/${q.id}`)}
                      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium"
                    >
                      <Icon name="Pencil" size={13} />Editar
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/crm/presupuestos/${q.id}`)}
                    className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                  >
                    <Icon name="Eye" size={13} />Ver / PDF
                  </button>
                  <button
                    onClick={() => handleDuplicate(q.id)}
                    disabled={!!busy}
                    className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <Icon name="Copy" size={13} />Duplicar
                  </button>
                  {q.status === 'borrador' && (
                    <button
                      onClick={() => handleStatus(q.id, 'enviado')}
                      disabled={!!busy}
                      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                    >
                      <Icon name="Send" size={13} />Marcar enviado
                    </button>
                  )}
                  {q.status === 'enviado' && (
                    <>
                      <button
                        onClick={() => handleStatus(q.id, 'aceptado')}
                        disabled={!!busy}
                        className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 disabled:opacity-50"
                      >
                        <Icon name="ThumbsUp" size={13} />Aceptado
                      </button>
                      <button
                        onClick={() => handleStatus(q.id, 'rechazado')}
                        disabled={!!busy}
                        className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
                      >
                        <Icon name="ThumbsDown" size={13} />Rechazado
                      </button>
                    </>
                  )}
                  {q.status === 'aceptado' && !q.converted_to_invoice_id && (
                    <button
                      onClick={() => handleCreateInvoice(q)}
                      disabled={!!busy}
                      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 font-medium"
                    >
                      <Icon name="ArrowRightCircle" size={13} />Crear nota de venta
                    </button>
                  )}
                  {q.converted_to_invoice_id && (
                    <button
                      onClick={() => navigate(`/crm/facturas/${q.converted_to_invoice_id}`)}
                      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 font-medium"
                    >
                      <Icon name="CheckCircle2" size={13} />
                      Nota de venta creada{q.crm_invoices?.invoice_number != null ? ` · ${formatInvoiceNumber(q.crm_invoices.invoice_number)}` : ''}
                    </button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </DashboardLayoutContent>
    </DashboardAppShell>
  );
}
